// Bin type registry (mig 175). Lists all active bin types - both
// system-wide rows (grower_id NULL) and grower-specific rows. Used by
// the BinInventory pickers to surface custom types alongside the fixed
// container_type enum.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Mig 475 — where a type is usable: INBOUND (delivery/reject-bin pickers),
// OUTBOUND (reject cage + dispatch pickers), BOTH (everywhere).
export type BinFlowRole = 'INBOUND' | 'OUTBOUND' | 'BOTH'

export interface BinTypeRow {
  bin_type_id:       string
  code:              string
  label:             string
  tare_kg:           number
  grower_id:         string | null
  is_kf49_owned:     boolean
  is_active:         boolean
  display_order:     number
  notes:             string | null
  is_system_default: boolean
  tenant_id:         string | null   // NULL = global baseline; set = tenant-owned (mig 20260629160000)
  flow_role:         BinFlowRole
}

export function useBinTypes(opts?: { onlyActive?: boolean; growerId?: string | null; enabled?: boolean }) {
  const onlyActive = opts?.onlyActive ?? true
  const growerId   = opts?.growerId   ?? undefined
  return useQuery({
    queryKey: ['bin-types', onlyActive, growerId ?? null],
    staleTime: 60_000,
    enabled: opts?.enabled ?? true,
    queryFn: async () => {
      // is_system_default added mig 285; tolerated missing on stragglers
      // so this hook can ship before the migration lands everywhere.
      let q = supabase.from('bin_types')
        .select('bin_type_id, code, label, tare_kg, grower_id, is_kf49_owned, is_active, display_order, notes, is_system_default, tenant_id, flow_role')
        .order('display_order', { ascending: true })
        .order('label',         { ascending: true })
      if (onlyActive) q = q.eq('is_active', true)
      const { data, error } = await q
      if (error) {
        if (/column.*is_system_default/i.test(error.message)) {
          const fallback = await supabase.from('bin_types')
            .select('bin_type_id, code, label, tare_kg, grower_id, is_kf49_owned, is_active, display_order, notes')
            .order('display_order', { ascending: true })
            .order('label',         { ascending: true })
          if (fallback.error) throw fallback.error
          // flow_role (mig 475) predates is_system_default (mig 285) being
          // missing, so a straggler without the latter won't have it either.
          return ((fallback.data ?? []) as Omit<BinTypeRow, 'is_system_default' | 'tenant_id' | 'flow_role'>[])
            .filter(r => !onlyActive || r.is_active)
            .map(r => ({ ...r, is_system_default: false, tenant_id: null, flow_role: 'INBOUND' as const }))
        }
        throw error
      }
      const rows = ((data ?? []) as Array<Omit<BinTypeRow, 'is_system_default'> & { is_system_default: boolean | null }>)
        .map(r => ({ ...r, is_system_default: r.is_system_default ?? false }))
      // When a grower is supplied, filter to system-wide + that grower's rows.
      if (growerId !== undefined) {
        return rows.filter(r => r.grower_id === null || r.grower_id === growerId)
      }
      return rows
    },
  })
}

interface OutboundCageType { bin_type_id: string; label: string; is_kf49_owned: boolean }

// Cage types usable as an OUTBOUND container (flow_role OUTBOUND or BOTH),
// so a picker cannot offer an inbound KF49 bin as the destination. One query
// for the transfer, dispatch and buyer pickers; `scope` keeps their caches apart.
export function useOutboundCageTypes(scope: string, opts?: { staleTime?: number }) {
  return useQuery({
    queryKey: [`cage-types-outbound-for-${scope}`],
    ...(opts?.staleTime !== undefined ? { staleTime: opts.staleTime } : {}),
    queryFn: async () => {
      // bin_types has no deleted_at column — is_active handles retirement.
      const { data, error } = await supabase.from('bin_types')
        .select('bin_type_id, label, is_kf49_owned')
        .in('flow_role', ['OUTBOUND', 'BOTH'])
        .eq('is_active', true)
        .order('label')
      if (error) throw error
      return (data ?? []) as OutboundCageType[]
    },
  })
}
